import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Loader2, MapPin, ChevronRight, Bell, ChevronLeft } from 'lucide-react';

const EventCard = ({ title, date, location, imageUrls }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % imageUrls.length);
    }, 3000); 

    return () => clearInterval(interval);
  }, [imageUrls.length]);

  return (
    <Link 
      to="/events"
      className="bg-white shadow-lg rounded-lg overflow-hidden cursor-pointer hover:shadow-xl transition-shadow block"
    >
      <div className="h-48 overflow-hidden">
        <img 
          src={imageUrls[currentImageIndex]} 
          alt={title} 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-4">
        <p className="text-gray-600 mb-2 text-sm">{date}</p>
        <h3 className="text-xl font-semibold mb-3">{title}</h3>
        <div className="flex items-center text-gray-600 mb-4">
          <MapPin size={16} className="mr-2" />
          <span className="text-sm">{location}</span>
        </div>
        <div className="w-full">
          <button 
            className="w-full bg-orange-500 text-white px-6 py-2 rounded-md hover:bg-orange-600 transition-colors"
          >
            View Details
          </button>
        </div>
      </div>
    </Link>
  );
};

const EventCarousel = () => {
  const [currentGroup, setCurrentGroup] = useState(0);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestEvents, setLatestEvents] = useState([]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get('https://atabackend.quindl.com/events/get');
        
        // Filter out past events and sort by date
        const currentDate = new Date();
        const activeEvents = response.data.filter(event => {
          // Check if event has an end date and if it's in the future
          if (event.endDate) {
            return new Date(event.endDate) > currentDate;
          }
          // If no end date, check the event date
          return new Date(event.date) > currentDate;
        });
        
        // Sort and get latest events
        const sortedEvents = activeEvents
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 3);
        setLatestEvents(sortedEvents);

        // Set all active events for carousel
        setEvents(activeEvents);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching events:', error);
        setError(error);
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const eventGroups = React.useMemo(() => {
    const groups = [];
    for (let i = 0; i < events.length; i += 3) {
      groups.push(events.slice(i, i + 3));
    }
    return groups;
  }, [events]);

  const nextGroup = () => {
    setCurrentGroup((prev) => (prev + 1) % eventGroups.length);
  };

  const prevGroup = () => {
    setCurrentGroup((prev) => (prev - 1 + eventGroups.length) % eventGroups.length);
  };

  useEffect(() => {
    const timer = setInterval(nextGroup, 3000);
    return () => clearInterval(timer);
  }, [eventGroups]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 text-center">
        <div className="flex justify-center items-center h-64">
          <Loader2 className="animate-spin text-orange-500" size={48} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 text-center">
        <p className="text-red-500">Error loading events. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-center mb-2 text-orange-600">Events</h1>
        <p className="text-center text-gray-600 mb-6 px-4">
          All the Events organised by Adelaide Tamil Association
        </p>
        
        <div className="flex flex-col md:flex-row items-center justify-between mb-6 px-4">
          <div className="flex items-center mb-4 md:mb-0">
            <Bell className="text-yellow-600 mr-2" />
            <span className="italic text-gray-700">Don't miss out on these!</span>
          </div>
          <Link 
            to="/events"
            className="flex items-center text-gray-900 font-semibold cursor-pointer hover:text-orange-500 transition-colors"
          >
            <span className="mr-1">See All Events</span>
            <ChevronRight size={18} />
          </Link>
        </div>

        {events.length > 0 ? (
          <div className="relative">
            <div className="flex items-center justify-center">
              <button 
                onClick={prevGroup}
                className="absolute -left-8 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white shadow-xl hover:bg-gray-200 transition-colors hidden md:block"
                aria-label="Previous events"
              >
                <ChevronLeft size={24} />
              </button>

              <div className="flex overflow-hidden relative w-full">
                <div 
                  className="flex transition-transform duration-500 ease-in-out w-full"
                  style={{ transform: `translateX(-${currentGroup * 100}%)` }}
                >
                  {eventGroups.map((group, groupIndex) => (
                    <div 
                      key={groupIndex} 
                      className="grid grid-cols-1 md:grid-cols-3 w-full flex-shrink-0 gap-4 px-4"
                    >
                      {group.map((event) => (
                        <div key={event.id || event.title} className="w-full">
                          <EventCard 
                            title={event.title} 
                            date={formatDate(event.date)}
                            location={event.location} 
                            imageUrls={event.images} 
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <button 
                onClick={nextGroup}
                className="absolute -right-8 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-white shadow-xl hover:bg-gray-200 transition-colors hidden md:block"
                aria-label="Next events"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            <div className="flex justify-center mt-4">
              {eventGroups.map((_, index) => (
                <button
                  key={index}
                  className={`w-3 h-3 mx-1 rounded-full transition-colors ${
                    currentGroup === index ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                  onClick={() => setCurrentGroup(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-600">
            No events available at the moment.
          </div>
        )}
        
        <div className="text-center mt-12 md:mt-20 px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Community</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Connect, share, and grow with like-minded individuals. Your journey starts here!
          </p>
          <div className="flex justify-center mt-12 md:mt-16">
          <Link
            to="/membership"
            className="group relative overflow-hidden px-6 md:px-8 py-3 md:py-4 border-orange-500
                     border-2 text-base md:text-lg font-medium rounded-lg 
                     transition-all duration-300 bg-white text-orange-500
                     hover:bg-orange-500 hover:text-white hover:border-orange-500 transform hover:-translate-y-1"
          >
            <span className="relative z-10">Join Our Community</span>
          </Link>
        </div>
        </div>
      </div>
    </div>
  );
};

export default EventCarousel;